import { reportNumber } from "@/profitability/report-number";
import type { ReviewSpec } from "@/profitability/review/review-spec";
import type {
  ReportColumn,
  ReportDataset,
  ReportRow,
  ReportValue,
} from "@/profitability/types";
import { formatSummaryValue } from "@/profitability/utils";
import { roleColumn } from "./objects";

/*
 * What a table colours, and only from what the report declares: a row's
 * rate against the target it is held to, a result below zero, a count the
 * report says should be low, the flag. Never from a column's name, and never
 * every figure: colour that is everywhere says nothing.
 */

/** A rate this close to its target is on it. */
const ON_TARGET = 0.0005;

export const rateColumn = (dataset: ReportDataset) =>
  dataset.columns.find((column) => column.key === dataset.rate_field);

export const targetColumn = (dataset: ReportDataset) =>
  dataset.columns.find((column) => column.key === dataset.target_field);

export interface RateJudgement {
  good: boolean;
  words: string;
}

export const judgeRate = (
  rate: number,
  target: number,
  better: "higher" | "lower",
): RateJudgement => {
  const gap = rate - target;
  const goal = formatSummaryValue({ value: target, type: "percent" });
  if (Math.abs(gap) < ON_TARGET)
    return { good: true, words: `On the ${goal} target` };
  return {
    good: better === "higher" ? gap > 0 : gap < 0,
    words: `${Math.abs(gap * 100).toFixed(1)} pts ${gap < 0 ? "under" : "over"} the ${goal} target`,
  };
};

export interface RateRule {
  rate: ReportColumn;
  better: "higher" | "lower";
  targetOf: (row: ReportRow) => number | null;
}

/**
 * How a dataset's rows are held to a target: its rate column, against the
 * row's own target column, else the Review metric's one target. Undefined
 * when there is no rate or nothing to hold it to: no target, no colour.
 */
export const rateRule = (
  dataset: ReportDataset,
  spec: ReviewSpec,
): RateRule | undefined => {
  const rate = rateColumn(dataset);
  if (!rate) return undefined;
  const target = targetColumn(dataset);
  const fixed = spec.metric.target;
  if (!target && fixed === null) return undefined;
  return {
    rate,
    better: rate.better ?? spec.metric.better ?? "higher",
    targetOf: (row) => (target ? reportNumber(row[target.key]) : null) ?? fixed,
  };
};

/** A row's rate against its target; null when either is missing. */
export const judgeRow = (rule: RateRule, row: ReportRow) => {
  const rate = reportNumber(row[rule.rate.key]);
  const target = rule.targetOf(row);
  return rate === null || target === null
    ? null
    : judgeRate(rate, target, rule.better);
};

/** The one target every row is held to, else null (each has its own). */
export const sharedTarget = (rule: RateRule, rows: ReportRow[]) => {
  const targets = rows.map(rule.targetOf);
  const [first] = targets;
  return first !== null &&
    first !== undefined &&
    targets.every((target) => target === first)
    ? first
    : null;
};

/**
 * The rate of the rows together: their results over their totals, leaving
 * out (and counting) the rows missing either (FIN-004).
 */
export const rateOfRows = (dataset: ReportDataset, rows: ReportRow[]) => {
  const total = roleColumn(dataset, "total");
  const result = roleColumn(dataset, "result");
  let sum = 0;
  let left = 0;
  let missing = 0;
  for (const row of rows) {
    const whole = total ? reportNumber(row[total.key]) : null;
    const part = result ? reportNumber(row[result.key]) : null;
    if (whole === null || part === null) {
      missing += 1;
      continue;
    }
    sum += whole;
    left += part;
  }
  return { rate: sum ? left / sum : null, missing };
};

/** A result (`role: 'result'`, such as gross profit) below zero: a loss. */
export const isLoss = (column: ReportColumn, value: ReportValue | undefined) =>
  column.role === "result" && (reportNumber(value) ?? 0) < 0;

/**
 * A count the report declares should be low (`better: 'lower'` on a plain
 * number, such as flagged jobs): any at all is worth a look.
 */
export const isWatchedCount = (
  column: ReportColumn,
  value: ReportValue | undefined,
) =>
  column.type === "number" &&
  !column.role &&
  column.better === "lower" &&
  (reportNumber(value) ?? 0) > 0;

/** Supporting text a table keeps quiet: dates and plain text columns. */
export const isQuiet = (column: ReportColumn) =>
  column.type === "date" ||
  (column.type === "text" && !column.flag && !column.references);
